import { MessagesSquare, Puzzle, Brain, ClipboardCheck } from "lucide-react";
import { Reveal, ChapterHeader } from "./Reveal";
import { IMAGES } from "@/lib/scroll";

const MODALITIES = [
  {
    icon: MessagesSquare,
    title: "Collaborative & narrative-informed",
    text: "Therapy is a shared journey. Together we give language to your story, honour it, and reconnect you with the strengths already within it.",
  },
  {
    icon: Puzzle,
    title: "Gestalt play techniques",
    text: "With younger clients, play becomes the conversation, a gentle and safe way for children to express what words cannot yet hold.",
  },
  {
    icon: Brain,
    title: "Cognitive Behavioural Therapy",
    text: "With adolescents and adults, CBT offers practical, structured tools to reduce distress and improve daily functioning.",
  },
];

export const Approach = () => (
  <section id="approach" data-testid="approach-section" className="py-24 md:py-32 bg-pawc-paper">
    <div className="max-w-6xl mx-auto px-6">
      <ChapterHeader number="03" eyebrow="How I Work" title="A respectful journey of rediscovery" />

      <div className="max-w-3xl mt-10 space-y-5 text-pawc-slate text-base md:text-lg leading-relaxed">
        <Reveal>
          <p>
            My approach is collaborative and narrative-informed, helping
            individuals share their stories and reconnect with their strengths.
            I draw on Gestalt play techniques with younger clients and Cognitive
            Behavioural Therapy (CBT) with adolescents and adults.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p>
            I also have experience in psychometric and psycho-legal assessment:
            careful, thorough evaluation work that supports clarity in educational,
            personal and legal contexts.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.15} className="mt-16">
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src={IMAGES.oceanWave}
            alt="A deep blue ocean wave seen from beneath the surface"
            className="w-full h-[340px] md:h-[420px] object-cover"
          />
          <div className="absolute inset-0 bg-pawc-deep/60" />
          <figure className="absolute inset-0 flex items-center justify-center px-8">
            <blockquote className="max-w-2xl text-center" data-testid="approach-quote">
              <p className="font-serif italic text-2xl md:text-4xl text-pawc-sand leading-snug">
                "It's the relationship that heals."
              </p>
              <cite className="not-italic text-xs tracking-[0.25em] uppercase text-pawc-mist/70 mt-4 block">
                Irvin D. Yalom
              </cite>
            </blockquote>
          </figure>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6 mt-16">
        {MODALITIES.map((m, i) => (
          <Reveal key={m.title} delay={0.1 + i * 0.1}>
            <article
              data-testid={`modality-card-${i}`}
              className="h-full rounded-2xl bg-pawc-sand border border-pawc-teal/10 p-8 shadow-[0_20px_40px_-15px_rgba(2,89,85,0.06)] hover:shadow-[0_25px_50px_-12px_rgba(2,89,85,0.12)] hover:-translate-y-1 transition-[transform,box-shadow] duration-500"
            >
              <span className="inline-flex w-11 h-11 rounded-full bg-pawc-teal/10 text-pawc-teal items-center justify-center mb-6">
                <m.icon size={20} strokeWidth={1.5} />
              </span>
              <h3 className="font-serif text-xl md:text-2xl text-pawc-deep mb-3">{m.title}</h3>
              <p className="text-sm md:text-base text-pawc-slate leading-relaxed">{m.text}</p>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-8 rounded-2xl border border-pawc-terra/30 bg-pawc-sand p-6 md:p-8 flex items-start gap-4">
          <ClipboardCheck size={20} strokeWidth={1.5} className="text-pawc-terra mt-1 shrink-0" />
          <p className="text-sm md:text-base text-pawc-slate leading-relaxed">
            <span className="text-pawc-deep font-medium">Assessment work:</span>{" "}
            psychometric, psychoeducational and psycho-legal (medico-legal)
            assessments and reporting are offered alongside therapy, with clear
            feedback sessions so results are understood, not just delivered.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
